import { html, LitElement, nothing, type PropertyValues, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, queryAsync, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../_ui-core_/base/package/util/a11y';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { localStorage_getOrNull } from '../../_ui-core_/base/package/util/localStorage';
import { NavBarIconLayout, NavBarIconName } from '../../_ui-core_/components/nav-bar/ui-nav-bar-icon';
import { openGamification, showGamificationButton } from '../../_ui-core_/mod-incentive/helper/gamification-launcher';
import App from '../../app';
import { MainRoute } from '../../app/package/base/router/router';
import { LoginStatus } from '../../app/package/base/service/login/login-domain';
import { ElementId } from '../../app/package/base/service/onboarding/onboarding-service';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import { STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE } from '../../constants';
import { type BasePageHeaderOptions, BasePageHeaderType } from './base-page-header';
import styles from './base-page-sidebar.css?inline';
import '../../_ui-core_/components/nav-bar/ui-gamification-icon';
import '../../_ui-core_/components/ui-lugas-session-timer/ui-lugas-session-timer';
import '../../_ui-core_/components/ui-screen-reader-only/ui-screen-reader-only';
import '../../_ui-core_/components/ui-spinner/ui-spinner';

const CName = 'base-page-sidebar';

/**
 * @prop {string} balance - The balance of the user
 * @prop {LoginStatus} loginStatus - The login status of the user
 * @prop {TemplateResult} slideinView - The slide-in view to display
 * @prop {string} currentRouteName - Current route name
 * @fires navigate-account - Fired when the account button is clicked
 * @fires navigate-deposit - Fired when the deposit button is clicked
 * @fires navigate-home - Fired when the home icon is clicked
 * @fires navigate-login - Fired when the login button is clicked
 * @fires navigate-search - Fired when the search icon is clicked
 * @fires navigate-sports - Fired when the sports icon is clicked
 * @fires navigate-games - Fired when the games icon is clicked
 * @fires navigate-to - Fired when an icon with custom path is clicked
s */
@customElement(CName)
export class BasePageHeader extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String }) balance: string;
  @property({ type: String }) loginStatus: LoginStatus;
  @property({ type: Object }) slideinView: TemplateResult | typeof nothing = nothing;
  @property({ type: String }) headerType?: BasePageHeaderType;
  @property({ type: String }) currentRouteName: string;
  @property({ type: Boolean }) slideInOnly = false;
  @property({ type: Object }) welcomeOfferOverride?: { title: string; subtitle: string };

  @state() private _loginStatus: LoginStatus = LoginStatus.UNDEFINED;
  @state() private _hasNewNotifications = false;
  @state() private _shouldShowGamification = false;
  @state() private _headerOptions?: BasePageHeaderOptions = undefined;

  @queryAsync(`#${ElementId.HEADER_USER_ICON}`) private readonly _headerUserIconEl?: Promise<HTMLElement>;
  @queryAsync(`#${ElementId.GAMIFICATION_ICON_DESKTOP}`) private readonly _gamificationIconEl?: Promise<HTMLElement>;
  @queryAsync(`#${ElementId.DEPOSIT_ICON}`) private readonly _depositIconEl?: Promise<HTMLElement>;

  private readonly _slideInRef = createRef();

  connectedCallback() {
    super.connectedCallback();
    if (this.slideInOnly) return;
    App.instance.login.getFinalLoginStatus().then((status) => {
      this._loginStatus = status;
      const showGamification = showGamificationButton();
      this._shouldShowGamification = showGamification && App.instance.featureFlag.allowMissions();
      if (!this._shouldShowGamification) {
        window.$app.onboarding.skipElement(ElementId.GAMIFICATION_ICON_DESKTOP);
      }
      if (status === LoginStatus.LOGGED_IN) {
        this._registerOnboardingElements();
      }

      if (App.instance.notifications !== undefined && status === LoginStatus.LOGGED_IN) {
        App.instance.notifications
          .getCurrentNewNotificationsCount()
          .then((c) => {
            this._hasNewNotifications = c > 0;
          })
          .catch((err) => {
            window.$app.logger.warn('Error fetching new notifications count', err);
          });
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.$app.onboarding.unregisterElement(ElementId.HEADER_USER_ICON);
    window.$app.onboarding.unregisterElement(ElementId.GAMIFICATION_ICON_DESKTOP);
    window.$app.onboarding.unregisterElement(ElementId.DEPOSIT_ICON);
  }

  updated(changed: PropertyValues) {
    if (changed.has('slideinView') && this.slideinView) {
      this._focusSlideIn();
    }
  }

  render() {
    if (this.slideInOnly) {
      return html`
        <div class="sidebar-wrapper sidebar-wrapper--slide-in-only">
          ${this._renderSlidein()}
        </div>
        ${this._renderSlideinBackdrop()}
      `;
    }

    const isHomeRoute = App.instance.router.isHomeRoute();
    const isSearchRoute = this.currentRouteName === MainRoute.SEARCH;
    const isPromotionRoute = this.currentRouteName === this._promotionsSlug;
    return html`
      <div class="sidebar-wrapper">
        <nav class="nav" aria-label=${window.$app.$t.get(I18n.a11y.heading.navigation)}>
          <a href=${App.instance.router.getPathNavigateToHome()} class="logo" @click=${this._handleHomeClick}
            @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleHomeClick.bind(this, ev))}
          ><ui-screen-reader-only>${window.$app.$t.get(I18n.base.logoButton)}</ui-screen-reader-only></a>
          <ul class="group">${this._renderLoginDepositButtons()}</ul>
          <ul class="group">
            <li>
              <ui-nav-bar-icon
                class="nav-icon sidebar"
                ?active=${isHomeRoute}
                aria-current=${isHomeRoute ? 'page' : undefined}
                .href=${App.instance.router.getPathNavigateToHome()}
                .icon=${NavBarIconName.HOME}
                .layout=${NavBarIconLayout.HORIZONTAL}
                @click=${this._handleHomeClick}
                @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleHomeClick.bind(this, ev))}
                label=${window.$app.$t.get(I18n.navigation.navbar.home)}
              ></ui-nav-bar-icon>
            </li>
            <li>
              <ui-nav-bar-icon
                class="nav-icon sidebar"
                ?active=${isSearchRoute}
                aria-current=${isSearchRoute ? 'page' : undefined}
                .href=${App.instance.router.getPathNavigateToSearch()}
                .icon=${NavBarIconName.SEARCH}
                .layout=${NavBarIconLayout.HORIZONTAL}
                @click=${this._handleSearchClick}
                @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleSearchClick.bind(this))}
                label=${window.$app.$t.get(I18n.navigation.navbar.search)}
              ></ui-nav-bar-icon>
            </li>
            ${
              App.instance.featureFlag.allowPromos()
                ? html`
                    <li>
                      <ui-nav-bar-icon
                        class="nav-icon sidebar"
                        ?active=${isPromotionRoute}
                        aria-current=${isPromotionRoute ? 'page' : undefined}
                        .href=${App.instance.router.getPathNavigateToPromotions()}
                        .icon=${NavBarIconName.PROMO}
                        .layout=${NavBarIconLayout.HORIZONTAL}
                        @click=${this._handlePromoClick}
                        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handlePromoClick.bind(this))}
                        label=${window.$app.$t.get(I18n.navigation.navbar.promo)}
                      ></ui-nav-bar-icon>
                    </li>
                  `
                : nothing
            }
            ${
              this._shouldShowGamification
                ? html`
                    <li>
                      <div
                        @click=${() => this._handleGamificationButtonClick()}
                        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, () => this._handleGamificationButtonClick())}
                        aria-label=${window.$app.$t.get(I18n.navigation.navbar.missions)}
                        class="missions-button-panel"
                        id=${ElementId.GAMIFICATION_ICON_DESKTOP}
                        role="button"
                        tabindex="0"
                      >
                        <ui-gamification-icon
                          ?isActive=${this._loginStatus === LoginStatus.LOGGED_IN}
                        ></ui-gamification-icon>
                        <span>${window.$app.$t.get(I18n.navigation.navbar.missions)}</span>
                      </div>
                    </li>
                  `
                : nothing
            }
          </ul>
          ${this.loginStatus === LoginStatus.LOGGED_IN ? this._renderUserButtons(this.currentRouteName) : nothing}
          <ul class="group">
            ${App.instance.featureFlag.showTipicoSportsLink() ? this._renderSportsNavItem() : nothing}
            ${App.instance.appSettings.isSHCasino ? this._renderGamesNavItem() : nothing}
          </ul>
          ${this._renderWelcomeOffer()}
          ${this.loginStatus === LoginStatus.LOGGED_IN ? this._renderLugasTimer() : nothing}
        </nav>
        ${this._renderSlidein()}
      </div>
      ${this._renderSlideinBackdrop()}
    `;
  }

  private _renderSportsNavItem() {
    return html`
      <li>
        <ui-nav-bar-icon
          class="nav-icon sidebar"
          .href=${window.$app.appConfig.sportsUrl}
          .icon=${NavBarIconName.SPORTS}
          .layout=${NavBarIconLayout.HORIZONTAL}
          @click=${this._handleSportsClick}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleSportsClick.bind(this))}
          label="Tipico Sports"
        ></ui-nav-bar-icon>
      </li>`;
  }

  private _renderGamesNavItem() {
    return html`
      <li>
        <ui-nav-bar-icon
          class="nav-icon sidebar"
          .href=${window.$app.appConfig.gamesUrl}
          .icon=${NavBarIconName.GAMES}
          .layout=${NavBarIconLayout.HORIZONTAL}
          @click=${this._handleGamesClick}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleGamesClick.bind(this))}
          label="Tipico Games"
        ></ui-nav-bar-icon>
      </li>`;
  }

  private _renderLugasTimer() {
    return html`<div class="lugas-session-timer-panel"><ui-lugas-session-timer></ui-lugas-session-timer></div>`;
  }

  private _renderSlidein() {
    if (!this.slideinView) {
      window.$app.modal.handleBodyScroll('unlock', 'slideIn');
      return nothing;
    }
    window.$app.modal.handleBodyScroll('lock', 'slideIn');
    return html`
      <div
        ${ref(this._slideInRef)}
        class="slide-in"
        role="region"
        aria-label=${this._headerOptions?.title || ''}
        data-route=${App.instance.router.getRouteName()}
        @header-options-update=${this._handleHeaderUpdate}
      >
        <base-page-header
          class="slide-in-header"
          .type=${this.headerType ?? BasePageHeaderType.DEFAULT}
          .options=${this._headerOptions}
          .secondaryHeader=${true}
          .slideInRef=${this._slideInRef}
        ></base-page-header>
        ${this.slideinView}
      </div>
    `;
  }

  private _renderSlideinBackdrop() {
    if (!this.slideinView) {
      return nothing;
    }
    return html`<div class="slide-in-backdrop" @click=${() => this._closeSlidein()}></div>`;
  }

  private _renderLoginDepositButtons() {
    if (this.loginStatus === LoginStatus.UNDEFINED || this.loginStatus === LoginStatus.FETCHING) {
      return html`<ui-spinner class="s"></ui-spinner>`;
    }
    if (this.loginStatus === LoginStatus.LOGGED_IN) {
      return this._renderDepositButton();
    }
    return this._renderLoginButton();
  }

  private _renderLoginButton() {
    return html`
      <li>
        <ui-btn
          @click=${this._handleLoginClick}
          variant="secondary-outlined"
          .isBlock=${true}
          class="login-button"
          data-testid="login"
        >
          ${window.$app.$t.get(I18n.base.login)}
        </ui-btn>
      </li>
    `;
  }

  private _renderDepositButton(): TemplateResult {
    const label = App.instance.router.isGamePageOpen() ? window.$app.$t.get(I18n.base.deposit) : this.balance;
    if (App.instance.featureFlag.showPayments()) {
      return html`
      <li>
        <ui-nav-bar-icon
          class="nav-icon sidebar"
          id=${ElementId.DEPOSIT_ICON}
          @click=${this._handleDepositClick}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleDepositClick.bind(this))}
          .icon=${NavBarIconName.DEPOSIT}
          label=${label}
          .layout=${NavBarIconLayout.HORIZONTAL}
        ></ui-nav-bar-icon>
      </li>
      `;
    }

    return html`
      <li>
        <ui-nav-bar-icon
          class="nav-icon no-action sidebar"
          ?active=${false}
          .icon=${NavBarIconName.WALLET}
          label=${label}
          .layout=${NavBarIconLayout.HORIZONTAL}
        ></ui-nav-bar-icon>
      </li>
    `;
  }

  private _renderUserButtons(currentRouteName: string) {
    return html`
      <ul class="group">
        <li>
          <ui-nav-bar-icon
            class="nav-icon sidebar"
            id=${ElementId.HEADER_USER_ICON}
            @click=${this._handleAccountClick}
            @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleAccountClick.bind(this))}
            ?active=${currentRouteName.startsWith('account')}
            .icon=${NavBarIconName.ACCOUNT}
            .layout=${NavBarIconLayout.HORIZONTAL}
            label=${window.$app.$t.get(I18n.base.account)}
          ></ui-nav-bar-icon>
        </li>
        ${
          App.instance.featureFlag.showNotifications()
            ? html`
            <li>
              <ui-nav-bar-icon
                class="nav-icon sidebar"
                @click=${this._goToNotifications}
                @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._goToNotifications.bind(this))}
                .icon=${NavBarIconName.NOTIFICATIONS}
                .layout=${NavBarIconLayout.HORIZONTAL}
                .showDot=${this._hasNewNotifications}
                label=${window.$app.$t.get(I18n.base.notifications)}
              ></ui-nav-bar-icon>
            </li>`
            : nothing
        }
      </ul>
    `;
  }

  private _renderWelcomeOffer() {
    if (this.loginStatus !== LoginStatus.LOGGED_OUT) {
      return nothing;
    }

    const hasUserLoggedInOnce = localStorage_getOrNull<boolean>(STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE);
    if (hasUserLoggedInOnce) {
      return;
    }

    const title = this.welcomeOfferOverride?.title ?? window.$app.$t.get(I18n.base.welcomeOffer.desktop.title);
    const subtitle = this.welcomeOfferOverride?.subtitle ?? window.$app.$t.get(I18n.base.welcomeOffer.desktop.subtitle);

    return html`
      <div class="welcome-offer">
        <div class="welcome-offer-icon">
          <ui-bonus-navigation-icon></ui-bonus-navigation-icon>
        </div>
        <p class="title">${unsafeHTML(title)}</p>
        <p>${subtitle}</p>
        <ui-btn .isBlock=${true} variant="dark" @click=${this._goToSignup}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._goToSignup.bind(this))}
        >${window.$app.$t.get(I18n.base.register)}</ui-btn>
      </div>
    `;
  }

  private get _promotionsSlug() {
    return App.instance.featureFlag.promotionsSlug;
  }

  private _goToNotifications() {
    window.$app.track.notifications('click');
    App.instance.product.gotoMessages();
  }

  private _goToSignup() {
    App.instance.product.gotoSignup(TrackingEventSource.MOBILE_BOTTOM_NAV);
  }

  private async _handleGamificationButtonClick() {
    window.$app.track.menuNavigation({
      eventAction: 'missions',
      navMode: App.instance.router.isGameRoute() ? 'gameMode' : 'normalMode',
    });
    await openGamification();
  }

  private _closeSlidein() {
    App.instance.router.closeSlideIn();
  }

  private _focusSlideIn() {
    this.updateComplete.then(() => {
      const slideIn = this._slideInRef.value as HTMLElement | undefined;
      if (!slideIn) return;
      if (!slideIn.hasAttribute('tabindex')) {
        slideIn.setAttribute('tabindex', '-1');
      }
      slideIn.focus();
    });
  }

  private _handleHeaderUpdate(ev: CustomEvent<BasePageHeaderOptions>) {
    window.$app.logger.log('Handle header update:', ev.detail);
    this._headerOptions = { ...ev.detail };
  }

  private _handleHomeClick(ev: Event) {
    ev.preventDefault();
    dispatchCustomEvent(this, 'navigate-home');
  }

  private _handleLoginClick() {
    dispatchCustomEvent(this, 'navigate-login');
  }

  private _handleDepositClick() {
    dispatchCustomEvent(this, 'navigate-deposit');
  }

  private _handleSearchClick() {
    window.$app.track.menuNavigation({ eventAction: 'search', navMode: 'normalMode' });
    dispatchCustomEvent(this, 'navigate-search');
  }

  private _handleSportsClick() {
    window.$app.track.menuNavigation({ eventAction: 'XSell', navMode: 'normalMode' });
    dispatchCustomEvent(this, 'navigate-sports');
  }

  private _handleGamesClick() {
    dispatchCustomEvent(this, 'navigate-games');
  }

  private _handleAccountClick() {
    window.$app.track.menuNavigation({ eventAction: 'account', navMode: 'normalMode' });
    dispatchCustomEvent(this, 'navigate-account');
  }

  private _handlePromoClick() {
    window.$app.track.menuNavigation({ eventAction: 'promo', navMode: 'normalMode' });
    dispatchCustomEvent(this, 'navigate-to', { path: `/${this._promotionsSlug}` });
  }

  private async _registerOnboardingElements() {
    const headerUserIcon = await this._headerUserIconEl;
    const depositIcon = await this._depositIconEl;

    if (headerUserIcon) {
      window.$app.onboarding.registerElement(ElementId.HEADER_USER_ICON, headerUserIcon);
    }

    if (depositIcon) {
      window.$app.onboarding.registerElement(ElementId.DEPOSIT_ICON, depositIcon);
    }

    if (this._shouldShowGamification) {
      const gamificationIcon = await this._gamificationIconEl;
      if (gamificationIcon) {
        window.$app.onboarding.registerElement(ElementId.GAMIFICATION_ICON_DESKTOP, gamificationIcon);
      }
    }
  }

  // private _handleMissionsClick() {
  // window.$app.track.menuNavigation({ eventAction: 'mission', navMode: 'normalMode' });
  //   dispatchCustomEvent(this, 'navigate-to', { path: '/missions' });
  // }
}
